import * as React from 'react';
import { PagerInterface, paginate } from '../../Helpers/PaginationHelper';
import { PaginationConfiguration } from './PaginationConfiguration';
import { PaginationContextProvider } from './PaginationContextProvider';
import { PaginationKeyInterface } from './PaginationKeyInterface';
import { PaginationProps } from './PaginationProps';

interface PaginationContainerState<T> {
    itemsOnPage: T[],
    pager?: PagerInterface
}

interface PagerInfoInterface<T> {
    pager: PagerInterface,
    itemsOnPage: T[]
}

const defaultProps: Readonly<Partial<PaginationConfiguration>> = {
    initialPage: 1,
    pageSize: 10,
    maxPages: 10,
};

export class Pagination<T extends PaginationKeyInterface> extends React.Component<PaginationProps<T>, PaginationContainerState<T>> {
    constructor(props: Readonly<PaginationProps<T>>) {
        super(props);
        this.handleSelectPage = this.handleSelectPage.bind(this);

        this.state = {itemsOnPage: [], pager: undefined};
    }

    public componentDidMount() {
        // Set page if items array isn't empty
        const props = this.props;
        if (props.items && props.items.length) {
            this.setInitialPage();
        }
    }

    public componentDidUpdate(
        prevProps: Readonly<PaginationProps<T>>,
        prevState: Readonly<PaginationContainerState<T>>
    ) {
        const props = this.props;
        if (props.items !== prevProps.items) {
            this.setInitialPage();
        }
    }

    public render() {
        const state = this.state;
        const pager = state.pager;

        // If the pager object is not yet set, don't render anything
        if (!pager) {
            return null;
        }

        const props = this.props;
        console.debug('[Pagination] Render', state, props);

        const itemsOnPage = state.itemsOnPage;
        const {children, ...propsWithoutChildren} = props;

        return <PaginationContextProvider {...propsWithoutChildren}
                                          itemsOnPage={itemsOnPage}
                                          pager={pager}
                                          onSelectPage={this.handleSelectPage}>{children}</PaginationContextProvider>;
    }

    private handleSelectPage(pageNumber: number) {
        const {pager, itemsOnPage} = this.getPagerForPage(pageNumber);
        console.debug('[Pagination] Received page change: New page number ' + pageNumber, itemsOnPage);

        this.setState({pager, itemsOnPage});

        // Inform the owning component about the change
        const onChangePage = this.props.onChangePage;
        if (onChangePage) {
            onChangePage(pageNumber, itemsOnPage);
        }
    }

    private getPagerForPage(page: number): PagerInfoInterface<T> {
        const props = this.props;
        const items = props.items;
        const pageSize = props.pageSize || defaultProps.pageSize;
        const maxPages = props.maxPages || defaultProps.maxPages;

        // Get new pager object for specified page
        const pager = paginate(
            items.length,
            page,
            pageSize,
            maxPages
        );

        // Get slice of the items on the current page
        const itemsOnPage = items.slice(pager.startIndex, pager.endIndex + 1);

        return {pager, itemsOnPage};
    }

    private setInitialPage() {
        const {pager, itemsOnPage} = this.getPagerForPage(this.getInitialPage());

        // Update state
        this.setState({pager, itemsOnPage});
    }

    private getInitialPage() {
        return this.props.initialPage || 1;
    }
}
